import { Grid, Link, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import { InputAdornment } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { useState, useEffect, useRef } from "react";

import Details from "./details";
import api from "../../../store/features/api";

import { handleError } from "src/utils/tokenRefreshLogic";
import UserManagment from "./UserManagement";
import CollapsibleCard from "./CollapsibleCard";
import { useGetMultiAccountUsersQuery } from "src/store/features/api/generated";
import { BANNER_STAGES, useContactChangeState } from "src/store/features/contactChange.slice";
import ContainedButton from "src/components/common/ContainedButton";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import useContactChange from "../contactChange/useContactChange";
import ContactChangeModal from "../contactChange/ContactChangeModal";
import { InfoSolidNavyIcon } from "src/icons/InfoSolidNavyIcon";
import { CheckSolidNavyIcon } from "src/icons/CheckSolidNavyIcon";
import { ExclamationMarkSolidNavyIcon } from "src/icons/ExclamationMarkSolidNavyIcon";
import Body from "./details/body";

interface DetailOptions {
  heading: string;
  picture?: string;
  fullName?: string;
  email?: string;
  phone?: string | undefined;
  phoneAU?: string | undefined;
  phoneUS?: string | undefined;
  address?: string | undefined;
}

const initialBrokerData = {
  broker: {
    email: "",
    name: "",
    phoneNumberAU: "",
    phoneNumberUS: "",
  },
  details: {
    customerId: "",
    dob: "",
    email: "",
    name: "",
    phone: "",
  },
  address: {
    fullAddress: "",
  },
};

const ContactChangeWrapper = styled("div")<{ background: string; isMobile: boolean }>(
  ({ background, isMobile }) => ({
    display: "flex",
    padding: "12px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: isMobile ? "16px" : "8px",
    alignSelf: "stretch",
    borderRadius: "10px",
    background: background,
  }),
);

const ContactChangeTextWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  flex: "1 0 0",
}));

const ContactChangeText = styled("div")<{ bold: boolean }>(({ bold }) => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: bold ? "500" : "400",
  lineHeight: "16px",
}));

const ContactChangeDetailsWrapper = styled("div")(() => ({
  display: "flex",
  padding: "0px",
  alignItems: "center",
  alignContent: "center",
  gap: "12px",
  alignSelf: "stretch",
  flexWrap: "wrap",
}));

const ContactChangeCTAWrapper = styled("div")<{ isMobile: boolean }>(({ isMobile }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  gap: "12px",
  ...(isMobile && {
    width: "100%",
  }),
}));

const OutlinedButtonWrapper = styled("button")(() => ({
  display: "inline-flex",
  width: "100%",
  minWidth: "140px",
  height: "36px",
  padding: "8px 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "20px",
  border: "1px solid #002035",
  cursor: "pointer",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "20px",
  background: "transparent",
  "&:hover": {
    background: "#F8F9F9",
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
}));

const PleaseNoteWrapper = styled("div")(() => ({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "10px",
  backgroundColor: "#F8F9F9",
}));

const PleaseNoteText = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "4px",
  flexWrap: "wrap",
  justifyContent: "flex-start",
}));

const PleaseNoteTitle = styled("span")(() => ({
  fontFamily: "Roobert, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "16px",
  color: "#002035",
  textDecoration: "underline",
}));

const PleaseNoteDescription = styled("span")(() => ({
  fontFamily: "Roobert, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "16px",
  color: "#002035",
}));

export type tabsType = "Profile" | "Security Settings" | "Payment" | "Useful Links";
export default function Profile() {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down(900));
  const { data: { broker, details, address } = initialBrokerData, error } =
    api.useGetUserDatav2Query();
  const { data, refetch } = useGetMultiAccountUsersQuery();
  const { bannerStage, contactChangeType, changingValueName, userEmail, requestedValue } =
    useContactChangeState();
  const {
    handleCancelContactChange,
    handleContinueButtonClick,
    cancelLoading,
    initiateLoading,
    latestContactChangeLoading,
  } = useContactChange();
  const [isCancelConfirmed, setIsCancelConfirmed] = useState(false);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cancelButtonRef.current && !cancelButtonRef.current.contains(event.target as Node)) {
        setIsCancelConfirmed(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  if (error) handleError(error);

  // Function to format data prop for <Details/>. for both client and broker
  const formatDetails = (options: DetailOptions) => {
    return {
      header: {
        heading: options.heading,
        picture: options.picture || "",
      },
      body: [
        ["Full Name", options.fullName],
        ["Email Address", options.email],
        options.phone ? ["Phone Number", options.phone || ""] : undefined,
        options.phoneAU ? ["Phone Number AU", options.phoneAU || ""] : undefined,
        options.phoneUS ? ["Phone Number US", options.phoneUS || ""] : undefined,
        options?.address ? ["Address", options?.address] : undefined,
      ],
    };
  };

  const CLIENT_DETAILS = formatDetails({
    heading: "My details",
    fullName: details.name,
    email: details.email,
    phone: details.phone,
    address: address ? address.fullAddress : undefined,
  });

  const BROKER_DETAILS = formatDetails({
    heading: "Broker details",
    fullName: broker ? broker.name : undefined,
    email: broker ? broker.email : undefined,
    phoneAU: broker ? broker.phoneNumberAU : undefined,
    phoneUS: broker ? broker.phoneNumberUS : undefined,
  });

  const handleCancelClick = () => {
    if (isCancelConfirmed) {
      handleCancelContactChange();
      setIsCancelConfirmed(false);
    } else {
      setIsCancelConfirmed(true);
    }
  };

  const ContactChangeBanner = () => {
    let cancelButtonText = "Cancel changes";
    if (cancelLoading) {
      cancelButtonText = "Cancelling...";
    } else if (isCancelConfirmed) {
      cancelButtonText = "Are you sure?";
    }

    switch (bannerStage) {
      case BANNER_STAGES.IN_PROGRESS:
        return (
          <ContactChangeWrapper background={"#FEFCF8"} isMobile={isMobile}>
            <ContactChangeTextWrapper>
              <InfoSolidNavyIcon />
              <ContactChangeText bold={true}>
                Contact change is currently in progress
              </ContactChangeText>
            </ContactChangeTextWrapper>
            <ContactChangeTextWrapper>
              <ContactChangeText bold={false}>
                You will receive an email notification to{" "}
                <span style={{ fontWeight: 500 }}>
                  {contactChangeType === "email" ? requestedValue : userEmail}
                </span>{" "}
                once this is done!
              </ContactChangeText>
            </ContactChangeTextWrapper>
          </ContactChangeWrapper>
        );
      case BANNER_STAGES.COMPLETED:
        return (
          <ContactChangeWrapper background={"#F0FEF9"} isMobile={isMobile}>
            <ContactChangeTextWrapper>
              <CheckSolidNavyIcon />
              <ContactChangeText bold={true}>Contact change is complete</ContactChangeText>
            </ContactChangeTextWrapper>
            <ContactChangeTextWrapper>
              <ContactChangeText bold={false}>
                There are limitations currently placed on your account for 24 hours after the change
                was processed.
              </ContactChangeText>
            </ContactChangeTextWrapper>
          </ContactChangeWrapper>
        );
      case BANNER_STAGES.ACTION_REQUIRED:
        return (
          <ContactChangeWrapper background={"#FFF6F6"} isMobile={isMobile}>
            <ContactChangeTextWrapper>
              <ExclamationMarkSolidNavyIcon />
              <ContactChangeText bold={true}>Action required</ContactChangeText>
            </ContactChangeTextWrapper>
            <ContactChangeDetailsWrapper>
              <ContactChangeTextWrapper>
                <ContactChangeText bold={false}>
                  Your contact change is incomplete, please complete the remaining step to finalise
                  changes to your {changingValueName}. Your contact change request will expire in a
                  week if no action is taken.
                </ContactChangeText>
              </ContactChangeTextWrapper>
              <ContactChangeCTAWrapper isMobile={isMobile}>
                <OutlinedButtonWrapper
                  ref={cancelButtonRef}
                  disabled={initiateLoading || cancelLoading || latestContactChangeLoading}
                  onClick={handleCancelClick}>
                  {cancelButtonText}
                </OutlinedButtonWrapper>
                <ContainedButton
                  color="navy"
                  borderRadius="20px"
                  height="36px"
                  disabled={initiateLoading || cancelLoading || latestContactChangeLoading}
                  onClick={handleContinueButtonClick}
                  endIcon={
                    <>
                      {initiateLoading && (
                        <InputAdornment position="end" style={{ width: 12 }}>
                          {initiateLoading && (
                            <CircularProgress size={12} style={{ color: "white" }} />
                          )}
                        </InputAdornment>
                      )}
                    </>
                  }>
                  Continue
                </ContainedButton>
              </ContactChangeCTAWrapper>
            </ContactChangeDetailsWrapper>
          </ContactChangeWrapper>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Grid container spacing={4}>
        {/* commented out for prod */}
        <>
          <ContactChangeModal />
          {bannerStage && (
            <Grid item xs={12} md={12} lg={12}>
              <ContactChangeBanner />
            </Grid>
          )}
        </>

        {/* ClIENT DETAILS */}
        <Grid item xs={12} md={12} lg={6} sx={isTabletOrMobile ? { margin: "0 16px" } : {}}>
          {isTabletOrMobile ? (
            <CollapsibleCard title="My details">
              <Body data={CLIENT_DETAILS.body} isUser />
            </CollapsibleCard>
          ) : (
            <Details header={CLIENT_DETAILS.header} body={CLIENT_DETAILS.body} isUser />
          )}
        </Grid>
        {/* BROKER DETAILS */}
        {broker && (
          <Grid item xs={12} md={12} lg={6} sx={isTabletOrMobile ? { margin: "0 16px" } : {}}>
            {isTabletOrMobile ? (
              <CollapsibleCard title="Broker details">
                <Body data={BROKER_DETAILS.body} isUser={false} />
              </CollapsibleCard>
            ) : (
              <Details header={BROKER_DETAILS.header} body={BROKER_DETAILS.body} />
            )}
          </Grid>
        )}
        <Grid item xs={12} md={12} lg={12} sx={isTabletOrMobile ? { margin: "0 16px" } : {}}>
          {isTabletOrMobile ? (
            <CollapsibleCard
              title="User Management"
              subtitle="Select a default account to be your primary account when you login.">
              <UserManagment user={data} refetch={refetch} hideHeader />
            </CollapsibleCard>
          ) : (
            <UserManagment user={data} refetch={refetch} />
          )}
        </Grid>

        {/* Please Note Component */}
        {true && (
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            sx={{
              paddingBottom: "16px",
              margin: "0px 16px",
            }}>
            <PleaseNoteWrapper>
              <PleaseNoteText>
                <InfoSolidNavyIcon />
                <PleaseNoteTitle>Please note</PleaseNoteTitle>
              </PleaseNoteText>
              <PleaseNoteDescription>
                Did you know you can{" "}
                <Link
                  href="#"
                  sx={{
                    color: "#002035",
                    textDecoration: "underline",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "16px",
                  }}>
                  set up multiple accounts
                </Link>
                ? Please contact your broker for more information.
              </PleaseNoteDescription>
            </PleaseNoteWrapper>
          </Grid>
        )}
      </Grid>
    </>
  );
}
